// src/api/apiService.js
import apiClient from "./axiosInstance";

/**
 * Fetch header and footer data from the API.
 * @param {string} locale - The locale for the content (e.g., 'en', 'zh-HK').
 * @returns {Promise<object>} The data object from the API.
 */
export const getHeaderFooterData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-header-and-footer";
  const requestParams = {
    locale: locale,
    "populate[header_menu_items][populate][sub_menu_items][populate]":
      "sub_menu_items",
    populate: "logo",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getHomepageData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-homepage";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "banner_image",
    "populate[featured_products_list][populate]": "product_image",
    "populate[solutions_list][populate]": "logo",
    populate: "partners",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getAboutUsData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-about-us";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "image",
    populate: "icon",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getOurProductsData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-product";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "image",
    "populate[products_categories][populate]": ["list", "logo"],
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getProductDetailPageTitles = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-product-detail";
  try {
    const responseData = await apiClient.get(endpoint, { params: { locale } });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getProductItemDetails = async (productName, locale = "en") => {
  const endpoint = "/aqutek-aisa-product-items";
  const requestParams = {
    locale: locale,
    "filters[name][$eq]": productName,
    "populate[downloads_files][populate]": "download_file",
    "populate[features_images][populate]": "image",
    "populate[banner][populate]": "image",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    if (responseData.data && responseData.data.length > 0) {
      return responseData.data[0];
    } else {
      console.warn(
        `[apiService] No product item found for name: ${productName}`
      );
      return null;
    }
  } catch (error) {
    throw error;
  }
};

export const searchAll = async (q, website = "aqutek-asia", locale = "zh-Hant") => {
  const endpoint = "/search/all";
  const params = { q, website, locale };
  try {
    const res = await apiClient.get(endpoint, { params });
    // Handle different response shapes returned by search endpoints
    // e.g. { data: [...] } or { results: [...] } or an array directly
    const hits = res?.data ?? res?.results ?? res?.hits ?? res ?? [];
    const arr = Array.isArray(hits) ? hits : hits.data ?? [];
    return arr;
  } catch (error) {
    throw error;
  }
};

export const getInfraDetailData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-infra-detail";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "image",
    "populate[infra_category][populate][infra_items][populate][datasheet][populate]":
      "download_file",
    "populate[infra_category][populate][infra_items][populate]": "image", // Populate infra_items 內部嘅 image
  };

  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getDownloadsPageData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-download";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "image",
    "populate[download_categories][populate][downloads_files][populate]":
      "download_file",
    "populate[download_categories][populate]": "logo",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getCaseStudyItemDetails = async (caseStudyName, locale = "en") => {
  const endpoint = "/aqutek-aisa-case-study-items";
  const requestParams = {
    locale: locale,
    "filters[name][$eq]": caseStudyName,
    "populate[banner][populate]": "image",
    "populate[diagrams][populate]": "image",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    if (responseData.data && responseData.data.length > 0) {
      return responseData.data[0];
    } else {
      console.warn(
        `[apiService] No case study item found for name: ${caseStudyName}`
      );
      return null;
    }
  } catch (error) {
    throw error;
  }
};

export const getContactUsPageData = async (locale = "en") => {
  const endpoint = "/aqutek-aisa-contact-us";
  const requestParams = {
    locale: locale,
    "populate[banner][populate]": "image",
    "populate[logo]": "true",
    "populate[online_enquiry]": "true",
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data;
  } catch (error) {
    throw error;
  }
};

export const getCountries = async (locale = "en") => {
  const endpoint = "/countries";
  const requestParams = {
    locale: locale,
  };
  try {
    const responseData = await apiClient.get(endpoint, {
      params: requestParams,
    });
    return responseData.data || [];
  } catch (error) {
    throw error;
  }
};

export const postOnlineEnquiry = async (data) => {
  const endpoint = "/online-enquiries";
  try {
    const responseData = await apiClient.post(endpoint, data);
    return responseData;
  } catch (error) {
    throw error;
  }
};
